/**
 * IMPORT PRACOWNIKÓW HURTEM — czysta reguła czytania wsadu, bez Reacta i bez sieci.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „dodawanie wielu pracowników jednocześnie przez pole tekstowe oraz
 *  import pliku z pracownikami w excelu", z podglądem „co na co się zmapuje".
 *
 *  ⚠️ DWIE DROGI, JEDNA REGUŁA. Pole tekstowe czyta POZYCYJNIE, Excel przez wskazane kolumny —
 *  ale sprawdzanie, wartości domyślne i podgląd muszą być wspólne. Dwa zestawy reguł znaczyłyby,
 *  że ten sam plik wpuszczony dwiema drogami daje w bazie klienta dwa różne wyniki.
 *
 *  ⚠️ PODSTAWA TO DWA PRAWDZIWE PLIKI OD DWÓCH FIRM (oba obejrzane, nie wyobrażone) — układ
 *  kolumn jest z nich przepisany, a wartości w przykładach to ATRAPY o tym samym kształcie,
 *  bo dane osobowe klienta nie wchodzą do repo:
 *    • arkusz `magna KJiIA0427.xlsx`: `Numer karty | Opis | grupa uzytkownikow | pozwol logowac |
 *      wylogoj po operacji | ograniczenie do pojedynczej przegrody | polityka wydzielania`.
 *      Nie ma aliasu ani centrum kosztowego, a `Opis` to POWTÓRZONY numer karty;
 *    • tabela z maila: `123456AB | Adam Nowak | ABCD1234 | 12345 | MONTER | 1 | 1 | 1`.
 *      Jest alias i centrum, jest grupa, NIE MA polityki wydzielania.
 *  Stąd kolejność pozycji niżej i stąd to, że prawie wszystko jest opcjonalne.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Klucze pól wsadu — te same w obu drogach i w podglądzie. */
export type KluczPola = 'karta' | 'nazwa' | 'alias' | 'centrum' | 'grupa' | 'logowanie' | 'wyloguj' | 'przegroda' | 'polityka';

export type OpisPola = {
  klucz: KluczPola;
  /** Klucz tłumaczenia etykiety — nazwy pól są napisami interfejsu, nie danymi. */
  etykieta: string;
  wymagane: boolean;
  /** Wartość wstawiana, gdy pozycji nie ma albo jest pusta. `null` = zostaw puste. */
  domyslnie: string | null;
};

/**
 * KOLEJNOŚĆ POZYCJI W WIERSZU TEKSTOWYM — CZĘŚĆ UMOWY Z CZŁOWIEKIEM, NIE SZCZEGÓŁ.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ GRUPA JEST PIĄTA I TO JEST ROZSTRZYGNIĘTA DECYZJA (04.09.2026), a nie oczywistość.
 *  Zamówienie brzmiało „karta, imię i nazwisko, alias, centrum kosztowe" plus cztery opcjonalne
 *  znaczniki — bez grupy. Ale OBA prawdziwe pliki grupę mają, a w wierszu z maila stoi ona
 *  dokładnie na piątym miejscu (`… 12345, MONTER, 1, 1, 1`). Bez tej pozycji wklejenie maila
 *  wprost wpisywałoby „MONTER" w „pozwól logować".
 *
 *  ⚠️ KOSZT TEJ DECYZJI JEST ZNANY I ZOSTAŁ PRZYJĘTY: wzór z zamówienia
 *  (`0001,Test testowy,Alias,666,1,1,1,3`) ma osiem pól, więc „1" wejdzie jako NAZWA GRUPY,
 *  takiej grupy nie będzie i import odmówi z listą brakujących. Odmowa jest widoczna w podglądzie
 *  PRZED wysłaniem — czyli mylny wsad zatrzymuje się na ekranie, a nie w bazie klienta.
 *
 *  ⚠️⚠️ WYMAGANY JEST JEDEN JEDYNY NUMER KARTY (dopowiedziane 04.09.2026: „można pomijać pola
 *  niewymagane i dać tylko numer karty, a reszta domyślna"). Wiersz `0001` jest poprawnym wsadem.
 *
 *  ⚠️⚠️ IMIĘ I NAZWISKO ZOSTAJE PUSTE, GDY GO NIE MA — i nic tam nie podstawiamy („nie masz tam
 *  nic wklejać domyślnie"). Kusiło wziąć numer karty, bo arkusz Magny tak robi (kolumna `Opis`
 *  to u nich powtórzony numer). Ale to jest zwyczaj JEDNEJ firmy, nie reguła: użytkownik pokazał
 *  na ekranie SPS-a użytkownika `0C` z PUSTYM opisem, czyli `Users.DESCR` wolno zostawić pusty.
 *  To NASZE API było za surowe (`name` z `min(1)`) — i zostało poluzowane, nie obejdzone.
 *
 *  ⚠️ Alias i centrum są `nullable` w schemacie tworzenia, a plik z Magny nie ma ich WCALE:
 *  gdyby były wymagane, jeden z dwóch znanych nam formatów nie dałby się wczytać.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const POLA_IMPORTU: readonly OpisPola[] = [
  { klucz: 'karta', etykieta: 'imp.pole.karta', wymagane: true, domyslnie: null },
  { klucz: 'nazwa', etykieta: 'imp.pole.nazwa', wymagane: false, domyslnie: null },
  { klucz: 'alias', etykieta: 'imp.pole.alias', wymagane: false, domyslnie: null },
  { klucz: 'centrum', etykieta: 'imp.pole.centrum', wymagane: false, domyslnie: null },
  { klucz: 'grupa', etykieta: 'imp.pole.grupa', wymagane: false, domyslnie: null },
  /** Cztery ostatnie mają wartości domyślne 1/1/1/3 — te same, co formularz pojedynczego pracownika. */
  { klucz: 'logowanie', etykieta: 'imp.pole.logowanie', wymagane: false, domyslnie: '1' },
  { klucz: 'wyloguj', etykieta: 'imp.pole.wyloguj', wymagane: false, domyslnie: '1' },
  { klucz: 'przegroda', etykieta: 'imp.pole.przegroda', wymagane: false, domyslnie: '1' },
  { klucz: 'polityka', etykieta: 'imp.pole.polityka', wymagane: false, domyslnie: '3' },
];

export const KLUCZE_POL = POLA_IMPORTU.map((p) => p.klucz);

/** Surowy wiersz: dla każdego pola napis albo `''`. Wspólny kształt obu dróg. */
export type SurowyWiersz = Record<KluczPola, string>;

export const pustyWiersz = (): SurowyWiersz =>
  Object.fromEntries(KLUCZE_POL.map((k) => [k, ''])) as SurowyWiersz;

/**
 * SEPARATOR — WYKRYWANY, ALE POKAZYWANY.
 *
 * ⚠️⚠️ TABULATOR MUSI BYĆ OBSŁUGIWANY, choć zamówienie mówi o przecinku. Wiersze z maila są
 * TABELĄ — skopiowanie ich do schowka daje kolumny rozdzielone TABULATOREM, nie przecinkiem.
 * Bez tego pierwsze realne wklejenie wpadłoby CAŁE w numer karty i wyglądałoby na usterkę pola.
 *
 * ⚠️ WYKRYCIE NIE MOŻE BYĆ MAGIĄ: okno pokazuje, czym rozdzieliło, więc gdy zgadniemy inaczej niż
 * człowiek myślał, widać to od razu w podglądzie — a nie po zapisaniu 200 osób.
 *
 * ⚠️ PIERWSZEŃSTWO MA TABULATOR, dopiero potem średnik, na końcu przecinek. Nazwiska przecinków
 * nie zawierają, ale wklejka z arkusza potrafi mieć oba znaki naraz (`Kowalski, Jan` w komórce),
 * a wtedy rozdzielenie przecinkiem rozerwałoby nazwisko na dwa pola.
 */
export type Separator = 'tab' | 'srednik' | 'przecinek';

export const ZNAK_SEPARATORA: Readonly<Record<Separator, string>> = { tab: '\t', srednik: ';', przecinek: ',' };

export function wykryjSeparator(tekst: string): Separator {
  if (tekst.includes('\t')) return 'tab';
  if (tekst.includes(';')) return 'srednik';
  return 'przecinek';
}

export type WierszWsadu = { nr: number; pola: string[] };

/**
 * Rozbicie wklejonego tekstu na wiersze i pozycje.
 *
 * ⚠️ NUMER WIERSZA LICZONY OD ORYGINAŁU, nie po odsianiu pustych. Komunikat „wiersz 7" musi
 * wskazywać to, co człowiek widzi w swoim polu tekstowym — inaczej odsyła w złe miejsce.
 */
export function podzielWiersze(tekst: string): { separator: Separator; wiersze: WierszWsadu[] } {
  const separator = wykryjSeparator(tekst);
  const znak = ZNAK_SEPARATORA[separator];
  const wiersze: WierszWsadu[] = [];
  tekst.split(/\r\n|\n|\r/).forEach((linia, i) => {
    if (linia.trim() === '') return;
    wiersze.push({ nr: i + 1, pola: linia.split(znak).map((p) => p.trim()) });
  });
  return { separator, wiersze };
}

/**
 * Pozycje → pola, w PODANEJ kolejności (domyślnie `POLA_IMPORTU`).
 *
 * ═══════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ DECYZJA ZMIENIONA 07.09.2026 I TO JEST WAŻNE DLA CZYTAJĄCEGO. 04.09.2026 kolejność była
 *  rozstrzygnięta jako STAŁA („tu chcemy na sztywno"), gdy proponowałem mapowanie do zmiany.
 *  Po pierwszych realnych plikach wróciło inne rozstrzygnięcie: „fajnie by było jeszcze, żeby
 *  zamieniać kolumny miejscami, bo różni klienci nam różnie to wysyłają". Przesłanka się zmieniła,
 *  nie kaprys: format nie jest własnością portalu, a klienta.
 *
 *  ⚠️ DOMYŚLNA KOLEJNOŚĆ ZOSTAJE TA SAMA (`POLA_IMPORTU`), bo pasuje do obu formatów, które mamy
 *  na papierze. Przestawianie jest wyjściem dla trzeciego klienta, nie nową normą.
 * ═══════════════════════════════════════════════════════════════════
 */
export function zPozycji(pola: readonly string[], kolejnosc: readonly KluczPola[] = KLUCZE_POL): SurowyWiersz {
  const w = pustyWiersz();
  kolejnosc.forEach((k, i) => {
    w[k] = pola[i] ?? '';
  });
  return w;
}

/**
 * PRZESTAWIENIE JEDNEJ POZYCJI — czysta zamiana sąsiadów.
 *
 * ⚠️ ZAMIANA SĄSIADÓW, NIE „przenieś na koniec": człowiek patrzy na kolejność kolumn w mailu
 * i przesuwa jedną o jedno miejsce, dopóki się nie zgadza. Większy skok wymagałby trzymania
 * w głowie, gdzie ta pozycja wyląduje.
 *
 * ⚠️ POZA ZAKRESEM ZWRACAMY LISTĘ BEZ ZMIAN, nie zawijamy. Zawinięcie z pierwszej pozycji na
 * ostatnią wygląda jak pomyłka narzędzia — przycisk na krańcu ma po prostu nic nie robić.
 */
export function przestaw(kolejnosc: readonly KluczPola[], i: number, kierunek: -1 | 1): KluczPola[] {
  const j = i + kierunek;
  if (i < 0 || i >= kolejnosc.length || j < 0 || j >= kolejnosc.length) return [...kolejnosc];
  const out = [...kolejnosc];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

/**
 * NUMER KARTY BEZ ZER WIODĄCYCH.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 07.09.2026 I DOMYŚLNIE WYŁĄCZONE: „niektórzy klienci nam wysyłają 0000AAA,
 *  bo tak im przeczytał jeden czytnik albo tak jest na karcie, a czytnik w automacie jest
 *  skonfigurowany na usuwanie zer wiodących".
 *
 *  ⚠️⚠️ DOMYŚLNIE WYŁĄCZONE NIE JEST OSTROŻNOŚCIĄ, A KONIECZNOŚCIĄ. Arkusz Magny ma numery w postaci
 *  `000001234` i tam zera SĄ CZĘŚCIĄ KLUCZA — obcinanie ich założyłoby ludzi pod numerem `1234`,
 *  czyli pod innym kluczem niż ten na karcie. To jest ta sama pomyłka, o której ostrzega
 *  ostrzeżenie o liczbowej kolumnie w arkuszu, tylko zrobiona przez nas.
 *
 *  ⚠️⚠️ ZOSTAWIAMY CO NAJMNIEJ JEDEN ZNAK. `0000` → `0`, nie pustka: numer karty pusty jest
 *  błędem wiersza, a przełącznik ma obcinać zera, nie kasować wierszy.
 * ═══════════════════════════════════════════════════════════════════
 */
export function bezZerWiodacych(v: string): string {
  const bez = v.replace(/^0+/, '');
  return bez === '' ? (v === '' ? '' : '0') : bez;
}

/**
 * ZNACZNIK 0/1 — z tolerancją na to, co realnie przychodzi w plikach.
 *
 * ⚠️ `true`/`false` SĄ TU Z POWODU EXCELA: komórka z zaznaczonym polem logicznym wraca jako
 * wartość logiczna, nie jako „1". Bez tego kolumna wyglądająca w arkuszu na wypełnioną byłaby
 * dla nas nierozpoznana.
 *
 * ⚠️ NIEROZPOZNANE WRACA JAKO `null`, NIE JAKO `false`. „MONTER" w kolumnie „pozwól logować"
 * musi być BŁĘDEM widocznym w podglądzie — potraktowane jako `false` założyłoby całej zmianie
 * ludzi bez prawa logowania do automatu i nikt by się o tym nie dowiedział przed maszyną.
 */
export function flaga(v: string): boolean | null {
  const s = v.trim().toLowerCase();
  if (s === '1' || s === 'true' || s === 'tak' || s === 'x') return true;
  if (s === '0' || s === 'false' || s === 'nie') return false;
  return null;
}

/** `Users.RATIONEFFECT`: 3 = egzekwuj limity, 2 = ignoruj ale loguj, 1 = ignoruj całkowicie. */
export function polityka(v: string): 1 | 2 | 3 | null {
  const s = v.trim();
  return s === '1' ? 1 : s === '2' ? 2 : s === '3' ? 3 : null;
}

/** ZNAKI SPOZA DRUKOWALNEGO ASCII — bliźniak `znakiNiedozwolone` z `config-down/klucze-naturalne.ts`. */
const ASCII = /^[\x20-\x7E]+$/;

export function zleZnakiKarty(v: string): string[] {
  return [...new Set([...v].filter((z) => !ASCII.test(z)))];
}

export const MAX_KARTA = 32;
export const MAX_NAZWA = 50;
export const MAX_ALIAS = 50;
export const MAX_CENTRUM = 50;

/**
 * MAKSYMALNA LICZBA OSÓB W JEDNYM WSADZIE.
 *
 * ⚠️ ISTNIEJE, BO CAŁY IMPORT JEDZIE JAKO **JEDNA** OPERACJA (tak rozstrzygnięte 04.09.2026):
 * agent stosuje ją w jednej transakcji na bazie SPS, więc albo jest cała kadra, albo nikogo.
 * Przy 20 automatach jedna osoba to ~22 wiersze zmian, czyli 1000 osób to ~22 000 wierszy
 * w jednej transakcji — to jeszcze mieści się w ciele żądania (25 MB) i w rozsądnym czasie
 * zapisu, a dwa razy tyle byłoby wróżeniem.
 */
export const MAX_WSAD = 1000;

/** Grupa pracownicza tak, jak jej szukamy: po nazwie ALBO po kodzie. */
export type GrupaDoDopasowania = { id: string; nazwa: string; kod: string };

export type KontekstWsadu = {
  /** Numery kart JUŻ ISTNIEJĄCE na wyspie — do rozpoznania, czy wiersz zakłada, czy aktualizuje. */
  istniejaceKarty: readonly string[];
  /**
   * ⚠️⚠️ OBECNE WARTOŚCI, po numerze karty małymi literami — do pokazania „stare → nowe"
   * i do rozpoznania wiersza, który NIC nie zmienia. Zamówione 08.09.2026: „fajnie byłoby
   * wykrywać jeżeli zupełnie nic się nie zmienia, bo czasem te gamonie 3x wysyłają do dodania
   * tę samą osobę".
   *
   * ⚠️ BRAK WPISU = NIE WIEMY, NIE „PUSTO". Kadrę ekran ciągnie do 500 osób, więc na większej
   * wyspie możemy znać kolizję numeru, a wartości nie. Wtedy „stare → nowe" się nie pokazuje,
   * bo nie ma czego pokazać.
   */
  obecni?: ReadonlyMap<string, ObecnyPracownik>;
  /**
   * ⚠️⚠️ POTRZEBNE DO PORÓWNANIA GRUPY, nie do zapisu. Przy „grupa z pliku zastępuje" brak
   * zmiany znaczy „ma dokładnie tę jedną"; przy „dodaj" wystarcza, że ją już ma. Bez tej flagi
   * wiersz odbierający komuś dwie inne grupy wyglądałby na „bez zmian".
   */
  grupyZastapuj?: boolean;
  /**
   * KODY CENTRÓW KOSZTOWYCH, KTÓRE NA WYSPIE JUŻ SĄ — kanonicznie (bez skrajnych spacji,
   * małymi literami), bo tak je porównuje kolacja SPS.
   *
   * ⚠️⚠️ `undefined` ZNACZY „NIE WIEMY" I NIE WOLNO GO CZYTAĆ JAKO „NIC NIE MA". Lista
   * przychodzi osobnym żądaniem, a okno importu otwiera się natychmiast — potraktowanie braku
   * odpowiedzi jako pustej wyspy oznaczyłoby KAŻDY wiersz jako nowe centrum i w dwa dni
   * nauczyłoby ten znaczek ignorować.
   */
  znaneCentra?: ReadonlySet<string>;
  grupy: readonly GrupaDoDopasowania[];
  /**
   * CZY WOLNO TKNĄĆ PRACOWNIKA, KTÓRY JUŻ JEST.
   *
   * ═══════════════════════════════════════════════════════════════
   *  ⚠️⚠️ ZGŁOSZONE 04.09.2026, I TO JEST REALNY OBYCZAJ KLIENTÓW, nie przypadek brzegowy:
   *  „firmy używają tych samych kart, a jak wskakuje nowy pracownik, to zmieniają tam tylko imię
   *  i nazwisko i grupę pracowniczą". Czyli plik z TĄ SAMĄ kartą i INNYM nazwiskiem jest
   *  poprawnym wsadem, a nie pomyłką.
   *
   *  ⚠️⚠️ DOMYŚLNIE `false` I TO JEST ŚWIADOME. Nadpisanie kadry jest nieodwracalne jednym
   *  ruchem, a wklejona lista „nowych ludzi", w której ktoś pomylił plik, wygląda dokładnie tak samo
   *  jak lista aktualizacji. Więc przy `false` zajęta karta jest BŁĘDEM — ale komunikat wskazuje
   *  ten przełącznik, żeby wyjście było o jedno kliknięcie, a nie do odgadnięcia.
   * ═══════════════════════════════════════════════════════════════
   */
  nadpisujIstniejace: boolean;
  /**
   * Czy obcinać zera wiodące z numeru karty.
   *
   * ⚠️⚠️ DOMYŚLNIE `false` I TO JEST KONIECZNOŚĆ, nie ostrożność — patrz `bezZerWiodacych`.
   * W arkuszu Magny numer karty ma postać `000001234` i to jest PEŁNY numer; obcinanie założyłoby tam ludzi pod innym
   * kluczem niż ten wybity na karcie.
   */
  usunZeraWiodace: boolean;
};

/**
 * DOPASOWANIE GRUPY — po nazwie i po kodzie, bez czułości na wielkość liter.
 *
 * ⚠️⚠️ PO OBU, BO NIE WIEMY, KTÓRE PODAJE KLIENT. W arkuszu Magny stoi `PRACOWNICY AB-CD`
 * (wygląda na nazwę), w mailu `MONTER` (wygląda na kod). Wymaganie jednego z nich znaczyłoby,
 * że jeden z dwóch znanych nam plików nie wchodzi.
 *
 * ⚠️ BEZ CZUŁOŚCI NA WIELKOŚĆ LITER, bo taka jest kolacja SPS — dla bazy klienta `MONTER`
 * i `Monter` to JEDEN wiersz, więc udawanie, że to dwie różne grupy, byłoby naszym wymysłem.
 *
 * ⚠️ DWIE PASUJĄCE GRUPY TO BŁĄD, NIE WYBÓR PIERWSZEJ. Gdy nazwa jednej grupy równa się kodowi
 * innej, „pierwsza z listy" wpisałaby ludzi do grupy wybranej kolejnością zapytania — czyli
 * uprawnienia na automacie rozdane losowo.
 */
export type DopasowanieGrupy = { stan: 'BRAK' } | { stan: 'JEDNA'; id: string; nazwa: string } | { stan: 'WIELE'; nazwy: string[] };

export function dopasujGrupe(szukane: string, grupy: readonly GrupaDoDopasowania[]): DopasowanieGrupy {
  const s = szukane.trim().toLowerCase();
  if (s === '') return { stan: 'BRAK' };
  const trafienia = grupy.filter((g) => g.nazwa.trim().toLowerCase() === s || g.kod.trim().toLowerCase() === s);
  if (trafienia.length === 0) return { stan: 'BRAK' };
  if (trafienia.length > 1) return { stan: 'WIELE', nazwy: trafienia.map((g) => g.nazwa) };
  return { stan: 'JEDNA', id: trafienia[0].id, nazwa: trafienia[0].nazwa };
}

/** Jedno pole w podglądzie: co wyszło i skąd. */
export type PodgladPola = {
  klucz: KluczPola;
  /** Wartość do pokazania; `null` = pole zostaje puste albo nietknięte. */
  wartosc: string | null;
  /**
   * Skąd ta wartość.
   *
   * ⚠️⚠️ `BEZ_ZMIAN` ISTNIEJE WYŁĄCZNIE DLA AKTUALIZACJI i jest najważniejszą różnicą między
   * zakładaniem a nadpisywaniem. Przy NOWYM pracowniku puste pole znaczy „wstaw domyślną";
   * przy AKTUALIZACJI znaczy „nie ruszaj tego, co jest". Gdyby aktualizacja też wstawiała
   * domyślne, poprawienie samego nazwiska przestawiałoby człowiekowi politykę wydzielania na 3
   * i włączało mu logowanie — bez żadnego objawu, aż ktoś stanie przed automatem.
   */
  zrodlo: 'WSAD' | 'DOMYSLNA' | 'PUSTE' | 'BEZ_ZMIAN';
  /** Komunikat dla TEGO pola — podgląd pokazuje błąd przy pozycji, której dotyczy. */
  blad: string | null;
  /**
   * Wartość SPRZED naszej poprawki albo `null`, gdy niczego nie ruszaliśmy.
   *
   * ⚠️⚠️ ISTNIEJE DLA OBCINANIA ZER WIODĄCYCH. Zmieniamy KLUCZ NATURALNY — numer karty —
   * i podgląd musi pokazać oba: to, co przysłał klient, i to, co założymy w SPS. Bez tego
   * przełącznik cicho przepisuje klucze i różnicę widać dopiero przy automacie.
   */
  przed: string | null;
  /**
   * ═════════════════════════════════════════════════════════════════
   *  CO SPS MA W TYM POLU TERAZ — `null` znaczy „nie wiemy albo nie dotyczy".
   *
   *  ⚠️⚠️ ZAMÓWIONE 08.09.2026: „na liście niech też się wyświetla stara i nowa wartość
   *  w przypadku nadpisania — nowa u góry na zielono […] i czerwone pod spodem".
   *
   *  ⚠️⚠️ TO INNE POLE NIŻ `przed` I NIE WOLNO ICH ZLAĆ. `przed` to wartość sprzed NASZEJ
   *  poprawki (obcięcie zer wiodących z pliku), a `obecne` to stan W BAZIE KLIENTA. Jedno mówi
   *  „tak było w pliku", drugie „tak jest w SPS" — przy nadpisywaniu oba mogą wystąpić naraz
   *  i znaczą co innego.
   *
   *  ⚠️⚠️ `null` NIE ZNACZY „PUSTE W BAZIE". Dwa pola (`wyloguj`, `przegroda`) nie są w liście
   *  kadry, którą ekran ma pod ręką, więc ich obecnej wartości NIE ZNAMY — i dlatego wiersz
   *  z nimi podanymi nie dostaje plakietki „bez zmian". Pokazanie tam kreski czytałoby się jako
   *  „w bazie jest pusto" i kazałoby nadpisywać coś, czego nikt nie widział.
   * ═════════════════════════════════════════════════════════════════
   */
  obecne: string | null;
  /**
   * ══════════════════════════════════════════════════
   *  CENTRUM KOSZTOWE, KTÓREGO NA WYSPIE JESZCZE NIE MA — pod żółty znaczek w podglądzie.
   *
   *  ⚠️⚠️ ZAMÓWIONE 08.09.2026: „chciałbym znaczek żółty ostrzeżenia, jeżeli danego centrum
   *  kosztowego jeszcze nie ma i zostanie utworzone przy danym użytkowniku".
   *
   *  ⚠️⚠️ TO OSTRZEŻENIE, NIE BŁĄD, i to jest cała różnica. Centrum kosztowe w SPS jest
   *  GOŁYM NAPISEM w `Users.COSTCENTER` — wolno tam wpisać cokolwiek i wpisanie nowego kodu jest
   *  normalną drogą zakładania centrum (słownik `UserCostCenters` jest u klientów PUSTY, kod stoi
   *  u ludzi z ręki). Blokowanie tego byłoby więc wymyślaniem reguły, której baza nie ma. Ale
   *  literówka w kodzie też przechodzi bez słowa i rozjazd widać dopiero w rozliczeniu — dlatego
   *  znaczek.
   *
   *  ⚠️ `false` ZNACZY „NIE JEST NOWE ALBO NIE WIEMY". Dopóki lista kodów nie dojdzie z serwera,
   *  nie ostrzegamy — patrz `czyNoweCentrum`.
   * ══════════════════════════════════════════════════
   */
  noweCentrum: boolean;
};

/**
 * STAN PRACOWNIKA W BAZIE KLIENTA — tyle, ile widzi lista kadry.
 *
 * ⚠️ BRAKUJE TU `wyloguj` I `przegroda` i to jest stan faktyczny, nie przeoczenie: `Users.
 * RETURNTOMENU` i `SINGLECOMPRESTRICT` nie są w read-modelu listy pracowników. Stąd `obecne`
 * dla nich zostaje `null`.
 */
export type ObecnyPracownik = {
  nazwa: string;
  alias: string | null;
  centrum: string | null;
  /** `Users.ALLOWLOGIN` — na liście kadry jako `active`. */
  logowanie: boolean;
  /** `Users.RATIONEFFECT`. */
  polityka: number;
  /** Nazwy grup pracowniczych, do których należy. */
  grupy: readonly string[];
};

/**
 * Pracownik gotowy do wysłania.
 *
 * ⚠️⚠️ POLA OPCJONALNE, BO POMINIĘTE ≠ PUSTE. Klucz nieobecny znaczy „nie ruszaj" (tak działa
 * `UpdateEmployeeSchema` w API), a `null` przy aliasie i centrum znaczy „wyczyść". Zlanie tych
 * dwóch rzeczy w jedno kasowałoby przy każdej aktualizacji nazwiska wszystko, czego plik nie
 * zawiera — a pliki klientów nie zawierają większości kolumn (arkusz Magny nie ma ani aliasu,
 * ani centrum kosztowego).
 *
 * ⚠️⚠️ `tryb` NIE JEDZIE DO API — to przewidywanie dla podglądu i tylko dla niego. Rozstrzyga
 * SERWER, po tym, czy karta w danej chwili istnieje; inaczej ekran mógłby kazać założyć kogoś,
 * kto właśnie powstał, albo nadpisać kogoś, kogo już nie ma. Schemat żądania jest `.strict()`,
 * więc to pole trzeba przy wysyłce zdjąć — i dobrze, bo dzięki temu nie da się go przemycić.
 */
export type PracownikDoWyslania = {
  spsUserNumber: string;
  tryb: 'NOWY' | 'AKTUALIZACJA';
  name?: string;
  alias?: string | null;
  costCenter?: string | null;
  active?: boolean;
  returnToMenu?: boolean;
  singleCompRestrict?: boolean;
  rationEffect?: 1 | 2 | 3;
  employeeGroupIds?: string[];
};

export type OcenaWiersza = {
  nr: number;
  pola: PodgladPola[];
  /** Błędy niezwiązane z jednym polem (np. nadmiarowe pozycje). */
  bledy: string[];
  /** Czy ten wiersz zakłada nową osobę, czy nadpisuje istniejącą — do plakietki w podglądzie. */
  tryb: 'NOWY' | 'AKTUALIZACJA';
  /**
   * ⚠️⚠️ WIERSZ, KTÓRY NIC NIE ZMIENIA — zamówione 08.09.2026 („czasem te gamonie nam 3x
   * wysyłają do dodania tę samą osobę i jeżeli calutki wiersz się zgadza to nie ma sensu
   * cokolwiek na takim pacjencie robić"). `false` znaczy „zmieni się coś" ALBO „nie wiemy",
   * i te dwa przypadki celowo się tu zlewają: oba kończą się tym, że wiersza nie wolno odsiać
   * po cichu. Powiedzenie „bez zmian" o czymś, czego nie widać w całości, byłoby gorsze
   * niż pytanie człowieka.
   */
  bezZmian: boolean;
  /**
   * Czy człowiek świadomie zdjął ten wiersz ze wsadu.
   *
   * ═══════════════════════════════════════════════════════════════
   *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „czasami też te gamonie wysyłają w jednym arkuszu zdublowane
   *  wiersze i to też będzie błąd — czy możemy zrobić jakiś mechanizm, który pozwoli odznaczyć
   *  błędny wiersz?".
   *
   *  ⚠️⚠️ TO NIE JEST ODWRÓT OD „ALBO CAŁY WSAD, ALBO NIC" — i ta różnica jest tu całą treścią.
   *  Wykluczenie jest decyzją CZŁOWIEKA podjętą PRZED wysłaniem: składa wsad, patrząc na podgląd.
   *  To, co wyjdzie, nadal jedzie JEDNĄ operacją i agent stosuje to w jednej transakcji.
   *  Czego portal nie zrobi, to sam nie wybierze, które wiersze pominąć — bo przy dwóch wierszach
   *  na tę samą kartę tylko człowiek wie, który z nich jest prawdziwy.
   * ═══════════════════════════════════════════════════════════════
   */
  wykluczony: boolean;
  /** `null`, gdy wiersz ma choć jeden błąd — nie wysyłamy połowy wiersza. */
  gotowy: PracownikDoWyslania | null;
};

const przytnij = (v: string) => v.trim();

/**
 * ═════════════════════════════════════════════════════════════════════════
 *  CZY TEN WIERSZ ZAŁOŻY NOWE CENTRUM KOSZTOWE.
 *
 *  ⚠️⚠️ BEZ LISTY KODÓW NIE ORZEKAMY NICZEGO — to jest najważniejsza linijka tej funkcji.
 *  `znane === undefined` znaczy „odpowiedź jeszcze nie przyszła albo odczyt padł", a nie
 *  „wyspa nie ma żadnego centrum". Ostrzeżenie postawione na niewiedzy zapaliłoby się przy
 *  każdym wierszu każdego wsadu — i przestałoby cokolwiek znaczyć.
 *
 *  ⚠️⚠️ PORÓWNUJEMY TAK, JAK PORÓWNUJE SPS: bez wielkości liter i bez skrajnych spacji, ale
 *  SPACJA W ŚRODKU JEST ZNACZĄCA. Dwa kody różniące się tylko nią to dla bazy DWA różne centra
 *  — zmierzone na warsztacie — i właśnie po to jest ten znaczek: taka para powstaje
 *  z literówki, a rozjazd widać dopiero w rozliczeniu kosztów.
 *
 *  ⚠️ TYLKO WARTOŚĆ Z WIERSZA (`WSAD`). `BEZ_ZMIAN` znaczy „nie ruszamy tego pola", więc
 *  żadnego centrum tam nie powstaje.
 * ═════════════════════════════════════════════════════════════════════════
 */
export function czyNoweCentrum(
  klucz: KluczPola,
  wartosc: string | null,
  zrodlo: PodgladPola['zrodlo'],
  znane: ReadonlySet<string> | undefined,
): boolean {
  if (klucz !== 'centrum' || zrodlo !== 'WSAD') return false;
  if (znane === undefined) return false;
  const kod = (wartosc ?? '').trim();
  if (kod === '') return false;
  return !znane.has(kod.toLowerCase());
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════
 *  CO SIĘ ZMIENI, A CO NIE — porównanie wsadu ze stanem w bazie klienta.
 *
 *  ⚠️⚠️ ZAMÓWIONE 08.09.2026, DWIE RZECZY NARAZ: „na liście niech też się wyświetla stara i nowa
 *  wartość w przypadku nadpisania" oraz „fajnie byłoby wykrywać jeżeli zupełnie nic się nie
 *  zmienia. Bo czasem te gamonie nam 3x wysyłają do dodania tę samą osobę i jeżeli calutki
 *  wiersz się zgadza to nie ma sensu cokolwiek na takim pacjencie robić".
 *
 *  ⚠️⚠️ „BEZ ZMIAN" ORZEKAMY TYLKO WTEDY, GDY WIDZIMY WSZYSTKO, CO WSAD PODAJE. Pole podane
 *  w pliku, którego obecnej wartości nie znamy (`wyloguj`, `przegroda` — nie ma ich w liście
 *  kadry), przekreśla cały werdykt. Inaczej portal mówiłby „nic się nie zmieni" o wierszu, który
 *  przestawia znacznik, o jakim nikt nie wie — czyli dokładnie ta klasa cichej pomyłki, przed
 *  którą broni tu cały podgląd.
 *
 *  ⚠️ PORÓWNUJEMY BEZ WIELKOŚCI LITER I BEZ SKRAJNYCH SPACJI — kolacja SPS jest CI, a wklejka
 *  z maila przynosi spacje na końcach pól. „Kowalski" i „kowalski " to w tej bazie ten sam napis,
 *  więc udawanie różnicy kazałoby nadpisywać bez powodu.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */
const ci = (v: string): string => v.trim().toLowerCase();

/** Obecna wartość tego pola jako napis; `null` = nie znamy albo nie dotyczy. */
function obecnaWartosc(klucz: KluczPola, o: ObecnyPracownik): string | null {
  if (klucz === 'nazwa') return o.nazwa;
  if (klucz === 'alias') return o.alias ?? '';
  if (klucz === 'centrum') return o.centrum ?? '';
  if (klucz === 'logowanie') return o.logowanie ? '1' : '0';
  if (klucz === 'polityka') return String(o.polityka);
  if (klucz === 'grupa') return o.grupy.join(', ');
  /** `karta` jest kluczem (z definicji ta sama), a `wyloguj` i `przegroda` nie są w read-modelu. */
  return null;
}

export function porownajZObecnym(
  pola: readonly PodgladPola[],
  obecny: ObecnyPracownik | null,
  grupyZastapuj: boolean,
): { pola: PodgladPola[]; bezZmian: boolean } {
  if (obecny === null) return { pola: [...pola], bezZmian: false };

  let wszystkoZnane = true;
  let cokolwiekSieZmieni = false;

  const wynik = pola.map((p) => {
    const obecne = obecnaWartosc(p.klucz, obecny);
    /** ⚠️ Nic nie podano — przy aktualizacji znaczy „nie ruszaj", więc nie ma czego porównywać. */
    if (p.zrodlo !== 'WSAD') return { ...p, obecne };
    if (p.klucz === 'karta') return { ...p, obecne: null };
    if (obecne === null) {
      /** ⚠️ Podane, a niewidoczne w bazie — werdykt „bez zmian" przestaje być uprawniony. */
      wszystkoZnane = false;
      return { ...p, obecne: null };
    }

    /**
     * ⚠️⚠️ GRUPA PORÓWNUJE SIĘ INACZEJ NIŻ RESZTA. Wsad podaje JEDNĄ grupę, a człowiek może
     * należeć do kilku. Przy „zastępuj" brak zmiany znaczy „ma dokładnie tę jedną"; przy
     * „dodaj" wystarcza, że ją już ma — bo pozostałe zostają nietknięte.
     */
    const rowne =
      p.klucz === 'grupa'
        ? grupyZastapuj
          ? obecny.grupy.length === 1 && ci(obecny.grupy[0]!) === ci(p.wartosc ?? '')
          : obecny.grupy.some((g) => ci(g) === ci(p.wartosc ?? ''))
        : ci(obecne) === ci(p.wartosc ?? '');

    if (!rowne) cokolwiekSieZmieni = true;
    return { ...p, obecne };
  });

  return { pola: wynik, bezZmian: wszystkoZnane && !cokolwiekSieZmieni };
}

/**
 * Ocena jednego wiersza: wartości, źródła i komunikaty.
 *
 * ⚠️ WSZYSTKIE BŁĘDY WIERSZA NARAZ, nie pierwszy napotkany. Człowiek poprawia wklejony wsad
 * w jednym przejściu; zgłaszanie po jednym błędzie na przebieg znaczyłoby tyle podejść, ile
 * literówek.
 */
export function ocenWiersz(nr: number, surowy: SurowyWiersz, kontekst: KontekstWsadu, wykluczony = false): OcenaWiersza {
  const pola: PodgladPola[] = [];
  const bledy: string[] = [];
  let poprawny = true;

  const zglos = (
    klucz: KluczPola,
    wartosc: string | null,
    zrodlo: PodgladPola['zrodlo'],
    blad: string | null,
    przed: string | null = null,
  ) => {
    /** ⚠️ `obecne` dokłada dopiero `porownajZObecnym` — tu nie znamy jeszcze trybu ani bazy. */
    pola.push({
      klucz,
      wartosc,
      zrodlo,
      blad,
      przed,
      obecne: null,
      noweCentrum: czyNoweCentrum(klucz, wartosc, zrodlo, kontekst.znaneCentra),
    });
    if (blad !== null) poprawny = false;
  };

  // ── karta: ona rozstrzyga, czy zakładamy, czy nadpisujemy ───────────────
  const wPliku = przytnij(surowy.karta ?? '');
  /**
   * ⚠️⚠️ OBCINAMY ZERA PRZED WSZYSTKIMI SPRAWDZENIAMI, bo od tej chwili `karta` JEST kluczem,
   * który założymy w SPS: po nim liczymy kolizję z wyspą, po nim duplikaty we wsadzie i po nim
   * jedzie `USERNUMBER`. Obcięcie PO sprawdzeniach dałoby portal, który sprawdził jeden numer,
   * a wysłał inny — najgorszy możliwy układ przy kluczu naturalnym.
   */
  const bezZer = kontekst.usunZeraWiodace ? bezZerWiodacych(wPliku) : wPliku;
  const zmienioneZera = bezZer !== wPliku ? wPliku : null;
  const zle = zleZnakiKarty(bezZer);
  /**
   * ⚠️ WIELKIE LITERY (08.10.2026, „ujednolić") — serwis i tak zapisze numer wielkimi, a podgląd ma
   * pokazywać TO, co powstanie w SPS. Po sprawdzeniu znaków, z tego samego powodu co na serwerze:
   * `ß` zamienione przed sprawdzeniem stałoby się poprawnym `SS`.
   */
  const karta = zle.length === 0 ? bezZer.toUpperCase() : bezZer;
  const kartaIstnieje = karta !== '' && kontekst.istniejaceKarty.some((k) => k.trim().toLowerCase() === karta.toLowerCase());
  /**
   * ⚠️⚠️ TRYB WYNIKA Z KARTY, NIE Z WYBORU CZŁOWIEKA. Przełącznik mówi tylko, czy WOLNO tknąć
   * istniejących; które wiersze są aktualizacją, wie baza. Wsad od klienta bywa MIESZANY — nowi
   * ludzie na nowych kartach i podmiany na starych w jednym pliku — i to jest normalny przypadek.
   */
  const tryb: 'NOWY' | 'AKTUALIZACJA' = kartaIstnieje && kontekst.nadpisujIstniejace ? 'AKTUALIZACJA' : 'NOWY';
  const aktualizacja = tryb === 'AKTUALIZACJA';

  zglos(
    'karta',
    karta === '' ? null : karta,
    karta === '' ? 'PUSTE' : 'WSAD',
    karta === ''
      ? 'imp.blad.kartaPusta'
      : karta.length > MAX_KARTA
        ? 'imp.blad.kartaDluga'
        : zle.length > 0
          ? `imp.blad.kartaZnaki|${zle.join(' ')}`
          : /**
             * ⚠️⚠️ ZAJĘTA KARTA JEST BŁĘDEM TYLKO BEZ ZGODY NA NADPISYWANIE — a komunikat wskazuje
             * wtedy przełącznik. Odmowa bez wskazania wyjścia jest tym samym błędem, który dzisiaj
             * naprawialiśmy przy nieudanych operacjach: ekran mówi „nie", nie mówiąc „jak".
             */
            kartaIstnieje && !kontekst.nadpisujIstniejace
            ? 'imp.blad.kartaZajeta'
            : null,
    /** ⚠️ Numer sprzed obcięcia zer — podgląd pokazuje oba, bo zmieniamy KLUCZ NATURALNY. */
    zmienioneZera,
  );

  /**
   * WARTOŚĆ POLA — i tu jest cała różnica między zakładaniem a nadpisywaniem.
   *
   * ⚠️⚠️ PRZY AKTUALIZACJI PUSTE POLE ZNACZY „NIE RUSZAJ", NIE „WSTAW DOMYŚLNĄ". Gdyby wstawiało
   * domyślną, podmiana samego nazwiska na karcie przestawiałaby człowiekowi politykę wydzielania
   * na 3, włączała logowanie i ograniczenie do jednej przegrody — czyli plik z dwiema kolumnami
   * cicho przepisywałby cały wiersz. Pliki klientów NIE MAJĄ większości kolumn (arkusz Magny nie ma
   * ani aliasu, ani centrum kosztowego), więc to nie jest przypadek brzegowy, a reguła.
   */
  const wartoscPola = (klucz: KluczPola): { v: string; zrodlo: PodgladPola['zrodlo']; podane: boolean } => {
    const opis = POLA_IMPORTU.find((x) => x.klucz === klucz)!;
    const podane = przytnij(surowy[klucz] ?? '');
    if (podane !== '') return { v: podane, zrodlo: 'WSAD', podane: true };
    if (aktualizacja) return { v: '', zrodlo: 'BEZ_ZMIAN', podane: false };
    if (opis.domyslnie !== null) return { v: opis.domyslnie, zrodlo: 'DOMYSLNA', podane: false };
    return { v: '', zrodlo: 'PUSTE', podane: false };
  };

  /**
   * ── imię i nazwisko (`Users.DESCR`) ────────────────────────────────────────
   *
   * ⚠️ POLSKIE LITERY TU WOLNO — to nie jest klucz naturalny. Zakaz znaków spoza ASCII dotyczy
   * WYŁĄCZNIE numeru karty (`USERNUMBER`), bo tylko on przechodzi przez kolację SPS, plik stanu
   * agenta i predykaty dziennika zmian. „Ewa Kowalska" z maila musi wejść jak stoi.
   *
   * ⚠️⚠️ PUSTE NIE JEST BŁĘDEM. SPS pozwala zostawić `Users.DESCR` pusty — pokazane na sprzęcie
   * (użytkownik `0C` z pustym opisem) — więc portal nie ma prawa tego wymagać ani niczego tu
   * podstawiać. Wiersz `0001` zakłada pracownika BEZ nazwy, dokładnie jak przy automacie.
   */
  const nazwaP = wartoscPola('nazwa');
  zglos('nazwa', nazwaP.v === '' ? null : nazwaP.v, nazwaP.zrodlo, nazwaP.v.length > MAX_NAZWA ? 'imp.blad.nazwaDluga' : null);

  // ── alias i centrum kosztowe ────────────────────────────────────
  const aliasP = wartoscPola('alias');
  zglos('alias', aliasP.v === '' ? null : aliasP.v, aliasP.zrodlo, aliasP.v.length > MAX_ALIAS ? 'imp.blad.aliasDlugi' : null);
  const centrumP = wartoscPola('centrum');
  zglos(
    'centrum',
    centrumP.v === '' ? null : centrumP.v,
    centrumP.zrodlo,
    centrumP.v.length > MAX_CENTRUM ? 'imp.blad.centrumDlugie' : null,
  );

  // ── grupa pracownicza ──────────────────────────────────────────
  const grupaWpis = przytnij(surowy.grupa ?? '');
  const dopasowanie = dopasujGrupe(grupaWpis, kontekst.grupy);
  const grupaId = dopasowanie.stan === 'JEDNA' ? dopasowanie.id : null;
  zglos(
    'grupa',
    grupaWpis === '' ? null : dopasowanie.stan === 'JEDNA' ? dopasowanie.nazwa : grupaWpis,
    grupaWpis === '' ? (aktualizacja ? 'BEZ_ZMIAN' : 'PUSTE') : 'WSAD',
    grupaWpis === ''
      ? null
      : dopasowanie.stan === 'BRAK'
        ? `imp.blad.grupaBrak|${grupaWpis}`
        : dopasowanie.stan === 'WIELE'
          ? `imp.blad.grupaWiele|${dopasowanie.nazwy.join(', ')}`
          : null,
  );

  // ── trzy znaczniki 0/1 ────────────────────────────────────────
  const znaczniki: Partial<Record<'logowanie' | 'wyloguj' | 'przegroda', boolean>> = {};
  for (const klucz of ['logowanie', 'wyloguj', 'przegroda'] as const) {
    const { v, zrodlo } = wartoscPola(klucz);
    if (v === '') {
      zglos(klucz, null, zrodlo, null);
      continue;
    }
    const b = flaga(v);
    if (b === null) zglos(klucz, v, zrodlo, `imp.blad.znacznik|${v}`);
    else {
      znaczniki[klucz] = b;
      zglos(klucz, b ? '1' : '0', zrodlo, null);
    }
  }

  // ── polityka wydzielania ─────────────────────────────────────
  const polP = wartoscPola('polityka');
  let pol: 1 | 2 | 3 | undefined;
  if (polP.v === '') zglos('polityka', null, polP.zrodlo, null);
  else {
    const w = polityka(polP.v);
    if (w === null) zglos('polityka', polP.v, polP.zrodlo, `imp.blad.polityka|${polP.v}`);
    else {
      pol = w;
      zglos('polityka', String(w), polP.zrodlo, null);
    }
  }

  /**
   * ⚠️⚠️ W ŻĄDANIU LĄDUJĄ TYLKO POLA, KTÓRE MAJĄ SIĘ ZMIENIĆ. Klucz nieobecny znaczy w API
   * „nie ruszaj" — i to jest jedyny sposób, żeby plik z dwiema kolumnami nie przepisał całego
   * wiersza pracownika. Przy NOWYM pracowniku wartości są zawsze (domyślne albo z wiersza).
   *
   * ⚠️ GRUPA WCHODZI TYLKO WTEDY, GDY WIERSZ JĄ PODAJE. Puste pole grupy przy aktualizacji NIE
   * MOŻE oznaczać pustej listy: `employeeGroupIds: []` w API znaczy „odbierz WSZYSTKIE grupy",
   * czyli podmiana nazwiska wyrzucałaby człowieka ze wszystkich grup.
   */
  const wyslijPole = <T,>(podane: boolean, wartosc: T): { obecne: true; wartosc: T } | { obecne: false } =>
    aktualizacja && !podane ? { obecne: false } : { obecne: true, wartosc };

  const skladaj = (): PracownikDoWyslania => {
    const out: PracownikDoWyslania = { spsUserNumber: karta, tryb };
    const nazwaWy = wyslijPole(nazwaP.podane, nazwaP.v);
    if (nazwaWy.obecne) out.name = nazwaWy.wartosc;
    const aliasWy = wyslijPole(aliasP.podane, aliasP.v === '' ? null : aliasP.v);
    if (aliasWy.obecne) out.alias = aliasWy.wartosc;
    const centrumWy = wyslijPole(centrumP.podane, centrumP.v === '' ? null : centrumP.v);
    if (centrumWy.obecne) out.costCenter = centrumWy.wartosc;
    if (znaczniki.logowanie !== undefined) out.active = znaczniki.logowanie;
    if (znaczniki.wyloguj !== undefined) out.returnToMenu = znaczniki.wyloguj;
    if (znaczniki.przegroda !== undefined) out.singleCompRestrict = znaczniki.przegroda;
    if (pol !== undefined) out.rationEffect = pol;
    if (grupaId !== null) out.employeeGroupIds = [grupaId];
    else if (!aktualizacja) out.employeeGroupIds = [];
    return out;
  };

  /**
   * ⚠️⚠️ WIERSZ WYKLUCZONY MA `gotowy: null`, ale to NIE JEST błąd — i tej różnicy pilnuje
   * `ocenWsad`. Pola i komunikaty liczymy DALEJ, żeby w podglądzie było widoczne, CO się odznaczyło
   * i dlaczego; wiersz zniknięty z tabeli po kliknięciu nie pozwalałby tego cofnąć świadomie.
   */
  /**
   * ═════════════════════════════════════════════════════════════════
   *  PORÓWNANIE Z BAZĄ — NA KOŃCU I TYLKO PRZY AKTUALIZACJI.
   *
   *  ⚠️⚠️ PRZY WIERSZU NOWYM NIE MA Z CZYM PORÓWNYWAć i „bez zmian" nie może tam wyjść. Karta
   *  wolna znaczy, że po zapisie powstanie ktoś, kogo teraz nie ma — nawet jeśli wszystkie pola
   *  są domyślne. Wiersz „nic nie zmienia" istnieje wyłącznie jako NADPISANIE tym samym.
   * ═════════════════════════════════════════════════════════════════
   */
  const obecny = aktualizacja ? (kontekst.obecni?.get(karta.trim().toLowerCase()) ?? null) : null;
  const porownane = porownajZObecnym(pola, obecny, kontekst.grupyZastapuj === true);

  return {
    nr,
    pola: porownane.pola,
    bledy,
    tryb,
    bezZmian: porownane.bezZmian,
    wykluczony,
    gotowy: poprawny && !wykluczony ? skladaj() : null,
  };
}

export type OcenaWsadu = {
  wiersze: OcenaWiersza[];
  /** Numery kart powtórzone WEWNĄTRZ wsadu → numery wierszy, w których się powtarzają. */
  duplikaty: { karta: string; wiersze: number[] }[];
  /** Nazwy grup z wsadu, których na wyspie nie ma — do jednego komunikatu, nie do 200. */
  brakujaceGrupy: string[];
  /** Ile wierszy WEJDZIE do wsadu — bez wykluczonych. */
  ile: number;
  /** Ile wierszy jest w podglądzie łącznie, razem z odznaczonymi. */
  wszystkich: number;
  wykluczonych: number;
  /** ⚠️ ROZBICIE NA ZAŁOŻENIA I NADPISANIA — bo to dwie różne rzeczy i tytuł operacji ma o nich mówić. */
  nowych: number;
  aktualizacji: number;
  bezZmian: number;
  /** Czy wolno wysłać: wszystkie wiersze poprawne, brak duplikatów, wsad niepusty i nie za duży. */
  mozna: boolean;
  zaDuzy: boolean;
};

/**
 * Ocena całego wsadu.
 *
 * ⚠️⚠️ ALBO CAŁY, ALBO NIC — i to nie jest ostrożność, a wymóg z kształtu tej zmiany. Import
 * jedzie jako JEDNA operacja config-down, więc agent zapisze go w całości albo wcale. Wysłanie
 * „tych poprawnych" znaczyłoby, że człowiek dostaje kadrę bez części ludzi i musi sam ustalić,
 * których brakuje — a numer karty jest kluczem naturalnym w czterech tabelach SPS, więc dosypanie
 * reszty później to nie jest ta sama czynność co jeden import.
 */
export function ocenWsad(
  surowe: readonly { nr: number; wiersz: SurowyWiersz }[],
  kontekst: KontekstWsadu,
  /** Numery wierszy zdjętych ze wsadu przez człowieka — patrz `wykluczony` w `OcenaWiersza`. */
  wykluczone: ReadonlySet<number> = new Set(),
): OcenaWsadu {
  const wiersze = surowe.map(({ nr, wiersz }) => ocenWiersz(nr, wiersz, kontekst, wykluczone.has(nr)));

  /**
   * ⚠️ DUPLIKAT WEWNĄTRZ WSADU JEST OSOBNYM PRZYPADKIEM od „karta zajęta". Ta druga wynika ze
   * stanu wyspy, ta pierwsza z pliku — i tylko tę pierwszą człowiek poprawia w swoim arkuszu.
   * Bez rozdzielenia komunikat mówiłby „numer zajęty" o numerze, którego jeszcze nigdzie nie ma.
   */
  const poKarcie = new Map<string, number[]>();
  for (const w of wiersze) {
    /**
     * ⚠️⚠️ ODZNACZONY WIERSZ NIE TWORZY DUPLIKATU — i to jest cały sposób, w jaki człowiek
     * rozwiązuje zdublowany arkusz: zostawia jeden wiersz na kartę, resztę zdejmuje. Gdyby
     * odznaczone dalej się liczyły, przycisk zostawałby martwy i mechanizm nie dałby nic.
     */
    if (w.wykluczony) continue;
    const karta = w.pola.find((p) => p.klucz === 'karta')?.wartosc;
    if (karta === null || karta === undefined) continue;
    const klucz = karta.trim().toLowerCase();
    poKarcie.set(klucz, [...(poKarcie.get(klucz) ?? []), w.nr]);
  }
  const duplikaty = [...poKarcie.entries()]
    .filter(([, nry]) => nry.length > 1)
    .map(([karta, nry]) => ({ karta, wiersze: nry }));

  const brakujaceGrupy = [
    ...new Set(
      wiersze.filter((w) => !w.wykluczony).flatMap((w) =>
        w.pola
          .filter((p) => p.klucz === 'grupa' && p.blad?.startsWith('imp.blad.grupaBrak') === true)
          .map((p) => p.blad!.split('|')[1]),
      ),
    ),
  ];

  /**
   * ⚠️⚠️ WSZYSTKO PONIŻEJ LICZY SIĘ Z WIERSZY NIEWYKLUCZONYCH. To jest sedno mechanizmu
   * odznaczania: odznaczony wiersz nie wchodzi do wsadu, nie robi duplikatu, nie blokuje
   * przycisku i nie liczy się do granicy. Gdyby liczył się do czegokolwiek z tych czterech
   * rzeczy, odznaczanie nie rozwiązywałoby zdublowanego arkusza — a po to istnieje.
   */
  const wchodzace = wiersze.filter((w) => !w.wykluczony);
  const zaDuzy = wchodzace.length > MAX_WSAD;
  const wszystkieDobre = wchodzace.length > 0 && wchodzace.every((w) => w.gotowy !== null && w.bledy.length === 0);

  return {
    wiersze,
    duplikaty,
    brakujaceGrupy,
    ile: wchodzace.length,
    wszystkich: wiersze.length,
    wykluczonych: wiersze.length - wchodzace.length,
    nowych: wchodzace.filter((w) => w.tryb === 'NOWY').length,
    aktualizacji: wchodzace.filter((w) => w.tryb === 'AKTUALIZACJA').length,
    /** ⚠️ Ile wchodzących wierszy nie zmieniłoby niczego — do plakietki i do przycisku odznaczania. */
    bezZmian: wchodzace.filter((w) => w.bezZmian).length,
    mozna: wszystkieDobre && duplikaty.length === 0 && !zaDuzy,
    zaDuzy,
  };
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════
 *  PODMIANA JEDNEGO POLA W WKLEJONYM TEKŚCIE.
 *
 *  ⚠️⚠️ ZAMÓWIONE 08.09.2026: „w przypadku błędu grupy — chciałbym móc kliknąć na to i mieć
 *  podgląd grup jakie w ogóle występują, w tej chwili, żeby poprawić grupę to trzeba z pamięci
 *  wiedzieć jakie są". Sama lista by wystarczyła do PATRZENIA, ale nie do POPRAWIENIA — dlatego
 *  kliknięcie w grupę wpisuje ją w ten wiersz.
 *
 *  ⚠️⚠️ DZIAŁAMY NA TEKŚCIE, NIE NA WYNIKU OCENY, i to jest tu istotne: pole tekstowe zostaje
 *  jedynym źródłem prawdy o wsadzie. Gdyby poprawka siedziała osobno („nadpisanie wartości dla
 *  wiersza 3"), tekst i podgląd rozjechałyby się przy pierwszym ręcznym dopisaniu linijki, a to
 *  jest ekran, na którym człowiek wkleja i doklepuje ręcznie.
 *
 *  ⚠️ SEPARATOR PRZYCHODZI Z ZEWNĄTRZ, bo rozpoznaje go `wykryjSeparator` na całym wsadzie.
 *  Składanie z powrotem innym znakiem niż ten, którym rozdzielono, przestawiłoby WSZYSTKIE pola
 *  w tej linii — czyli poprawka jednej grupy zepsułaby cały wiersz.
 *
 *  ⚠️ KRÓTSZĄ LINIĘ DOPEŁNIAMY PUSTYMI POLAMI. Wiersz „karta, nazwisko" bez grupy ma tylko dwa
 *  pola, a grupa jest piąta — bez dopełnienia kliknięcie wpisałoby ją na trzeciej pozycji, czyli
 *  w alias. To ten sam rodzaj cichej pomyłki, przed którą broni sztywna kolejność pól.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */
export function podmienPole(
  tekst: string,
  nrWiersza: number,
  indeksPola: number,
  wartosc: string,
  separator: Separator,
): string {
  const znak = ZNAK_SEPARATORA[separator];
  /**
   * ⚠️ DZIELIMY PO `\n` I ZOSTAWIAMY `\r`, a potem odkładamy go z powrotem: wklejka z Windows
   * ma `\r\n`, a zjedzenie `\r` przestawiłoby ostatnie pole każdej linii.
   */
  const linie = tekst.split('\n');
  const i = nrWiersza - 1;
  if (i < 0 || i >= linie.length) return tekst;

  const surowa = linie[i]!;
  const zakonczenie = surowa.endsWith('\r') ? '\r' : '';
  const pola = surowa.slice(0, surowa.length - zakonczenie.length).split(znak);
  while (pola.length <= indeksPola) pola.push('');
  pola[indeksPola] = wartosc;

  linie[i] = pola.join(znak) + zakonczenie;
  return linie.join('\n');
}

/**
 * WIERSZE, KTÓRE NIC NIE ZMIENIAJĄ — do jednego kliknięcia „odznacz".
 *
 * ⚠️⚠️ ZAMÓWIONE 08.09.2026: „czasem te gamonie nam 3x wysyłają do dodania tę samą osobę
 * i jeżeli calutki wiersz się zgadza to nie ma sensu cokolwiek na takim pacjencie robić".
 *
 * ⚠️⚠️ PORTAL NIE ODZNACZA SAM — tak samo jak przy powtórzonych i błędnych. Wiersz bez zmian
 * jest nieszkodliwy (aktualizacja tymi samymi wartościami niczego nie psuje), więc odsianie go
 * po cichu zmieniałoby liczbę „zaimportowano N" bez pytania. To ma być jedno kliknięcie,
 * nie zachowanie.
 */
export function bezZmianDoOdznaczenia(ocena: OcenaWsadu): number[] {
  return ocena.wiersze.filter((w) => !w.wykluczony && w.bezZmian).map((w) => w.nr);
}

/**
 * NUMERY WIERSZY, KTÓRE POWTARZAJĄ NUMER KARTY — bez pierwszego wystąpienia.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ISTNIEJE, BO PRZY 2500 WIERSZACH ODZNACZANIE Z RĘKI NIE JEST INTERFEJSEM. Zgłoszenie
 *  mówiło o arkuszach z „zdublowanymi wierszami" — a plik, na którym to zmierzyliśmy, ma 2500
 *  wierszy. Jedno kliknięcie musi zdjąć wszystkie powtórzenia.
 *
 *  ⚠️ ZOSTAWIAMY PIERWSZE WYSTĄPIENIE, nie ostatnie, i to jest wybór do obrony: arkusze klientów
 *  są dopisywane od góry w dół, więc pierwszy wiersz jest tym, który ktoś wpisał świadomie,
 *  a dalsze bywają skutkiem kopiowania. Człowiek i tak widzi w podglądzie, co zostało.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function powtorzoneDoOdznaczenia(ocena: OcenaWsadu): number[] {
  const widziane = new Set<string>();
  const doOdznaczenia: number[] = [];
  for (const w of ocena.wiersze) {
    if (w.wykluczony) continue;
    const karta = w.pola.find((p) => p.klucz === 'karta')?.wartosc;
    if (karta === null || karta === undefined) continue;
    const klucz = karta.trim().toLowerCase();
    if (widziane.has(klucz)) doOdznaczenia.push(w.nr);
    else widziane.add(klucz);
  }
  return doOdznaczenia;
}

/**
 * NUMERY WIERSZY Z JAKIMKOLWIEK BŁĘDEM — do zdjęcia jednym kliknięciem.
 *
 * ⚠️ OSOBNO OD POWTÓRZEŃ, bo to inna decyzja: powtórzenie da się rozwiązać wyborem, a wiersz
 * z błędem trzeba albo poprawić w pliku, albo świadomie pominąć. Portal nie odznacza go sam —
 * pominięty człowiek nie trafi na automat i nikt się o tym nie dowie, dopóki nie stanie przed nim.
 */
export function bledneDoOdznaczenia(ocena: OcenaWsadu): number[] {
  return ocena.wiersze.filter((w) => !w.wykluczony && w.gotowy === null).map((w) => w.nr);
}
